/**
 * ProTracker MOD loader.
 *
 * Handles the 31-sample family (M.K. and its many channel-count variants) plus
 * the original 15-sample Soundtracker layout, which has no magic bytes at all
 * and has to be inferred.
 */
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  createInstrument,
  createPattern,
  createSample,
  LOOP_FORWARD,
  LOOP_NONE,
  type LoadResult,
  type LoadWarning,
  type Song,
} from '../types';
import { isOffTablePeriod, periodToNote } from '../constants';
import { ByteReader } from './binary';

const HEADER_15 = 600;
const HEADER_31 = 1084;
const ORDER_COUNT = 128;
const ROWS_PER_PATTERN = 64;

interface Variant {
  channels: number;
  /** StarTrekker's 8-channel format stores two 4-channel patterns per entry. */
  splitPatterns: boolean;
  label: string;
}

/** Decide the channel count from the 4-byte tag at offset 1080. */
export function variantFromMagic(magic: string): Variant | null {
  const m = magic.toUpperCase();
  switch (m) {
    case 'M.K.':
    case 'M!K!':
    case 'M&K!':
    case 'N.T.':
    case 'NSMS':
    case 'LARD':
    case 'PATT':
    case 'FEST':
    case 'FLT4':
    case 'EXO4':
      return { channels: 4, splitPatterns: false, label: m };
    case 'FLT8':
    case 'EXO8':
      return { channels: 8, splitPatterns: true, label: m };
    case 'OCTA':
    case 'OKTA':
    case 'CD81':
      return { channels: 8, splitPatterns: false, label: m };
    default:
      break;
  }
  // "6CHN", "8CHN"
  if (/^[1-9]CHN$/.test(m)) {
    return { channels: Number(m[0]), splitPatterns: false, label: m };
  }
  // "16CH" .. "32CH"
  if (/^[1-9][0-9]CH$/.test(m)) {
    return { channels: Number(m.slice(0, 2)), splitPatterns: false, label: m };
  }
  // TakeTracker "TDZ1".."TDZ9"
  if (/^TDZ[1-9]$/.test(m)) {
    return { channels: Number(m[3]), splitPatterns: false, label: m };
  }
  // "CD61" — 6 channel
  if (m === 'CD61') {
    return { channels: 6, splitPatterns: false, label: m };
  }
  return null;
}

/** Cheap sniff so the UI can route a dropped file without fully parsing it. */
export function isModFile(buffer: ArrayBuffer): boolean {
  const r = new ByteReader(buffer);
  if (r.length < HEADER_15) return false;
  if (r.length >= HEADER_31 && variantFromMagic(r.peekStr(1080, 4))) return true;
  return looksLike15Sample(r);
}

/**
 * A 15-sample module has no tag, so we check that the fields where a header
 * would be are all plausible: sane volumes, sane order count, and a sample
 * block that does not overrun the file.
 */
function looksLike15Sample(r: ByteReader): boolean {
  if (r.length < HEADER_15) return false;
  let totalSamples = 0;
  for (let i = 0; i < 15; i++) {
    const base = 20 + i * 30;
    const lengthWords = (r.bytes[base + 22] << 8) | r.bytes[base + 23];
    const finetune = r.bytes[base + 24];
    const volume = r.bytes[base + 25];
    if (finetune > 0x0f) return false;
    if (volume > 64) return false;
    totalSamples += lengthWords * 2;
  }
  const songLength = r.bytes[470];
  if (songLength === 0 || songLength > 128) return false;
  let maxPattern = 0;
  for (let i = 0; i < ORDER_COUNT; i++) {
    const p = r.bytes[472 + i];
    if (p > 63) return false; // Soundtracker never exceeds 64 patterns
    if (p > maxPattern) maxPattern = p;
  }
  const expected = HEADER_15 + (maxPattern + 1) * ROWS_PER_PATTERN * 4 * 4 + totalSamples;
  // Allow slack: many files are padded or slightly truncated.
  return r.length >= expected * 0.5;
}

export function loadMod(buffer: ArrayBuffer): LoadResult {
  const r = new ByteReader(buffer);
  const warnings: LoadWarning[] = [];

  const magic = r.length >= HEADER_31 ? r.peekStr(1080, 4) : '';
  let variant = variantFromMagic(magic);
  const sampleCount = variant ? 31 : 15;

  if (!variant) {
    if (!looksLike15Sample(r)) {
      throw new Error('Not a recognisable MOD file (no known tag and header is implausible)');
    }
    variant = { channels: 4, splitPatterns: false, label: 'Soundtracker' };
    warnings.push({
      code: 'st15',
      message: '15-sample Soundtracker module — channel layout inferred from the header.',
    });
  }

  const headerSize = sampleCount === 31 ? HEADER_31 : HEADER_15;
  const orderOffset = sampleCount === 31 ? 952 : 472;
  const lengthOffset = sampleCount === 31 ? 950 : 470;

  r.seek(0);
  const title = r.str(20);

  // --- Sample headers -----------------------------------------------------
  interface RawSample {
    name: string;
    length: number;
    finetune: number;
    volume: number;
    loopStart: number;
    loopLength: number;
  }
  const raw: RawSample[] = [];
  for (let i = 0; i < sampleCount; i++) {
    const name = r.str(22);
    const length = r.u16be() * 2;
    const ftNibble = r.u8() & 0x0f;
    const finetune = ftNibble > 7 ? ftNibble - 16 : ftNibble; // signed 4-bit
    const volume = Math.min(64, r.u8());
    const loopStart = r.u16be() * 2;
    const loopLength = r.u16be() * 2;
    raw.push({ name, length, finetune, volume, loopStart, loopLength });
  }

  // --- Order table --------------------------------------------------------
  r.seek(lengthOffset);
  let songLength = r.u8();
  const restartByte = r.u8();
  r.seek(orderOffset);
  const order = new Uint8Array(ORDER_COUNT);
  let maxPattern = 0;
  for (let i = 0; i < ORDER_COUNT; i++) {
    const p = r.u8();
    order[i] = p;
    if (p > maxPattern) maxPattern = p;
  }
  if (songLength === 0 || songLength > 128) {
    warnings.push({ code: 'songlen', message: `Song length ${songLength} out of range; clamped.` });
    songLength = Math.max(1, Math.min(128, songLength || 1));
  }

  let patternCount = maxPattern + 1;

  // --- "Mod's Grave" / WOW: an M.K. tag hiding 8 channels ------------------
  if (variant.label === 'M.K.') {
    const sampleBytes = raw.reduce((sum, s) => sum + s.length, 0);
    const patternBytes = r.length - HEADER_31 - sampleBytes;
    const fourChanSize = patternCount * ROWS_PER_PATTERN * 4 * 4;
    if (patternBytes >= fourChanSize * 2 - 16) {
      variant = { channels: 8, splitPatterns: false, label: 'M.K. (8ch/WOW)' };
      warnings.push({
        code: 'wow',
        message: "Detected a Mod's Grave 8-channel module wearing an M.K. tag.",
      });
    }
  }

  const channels = variant.channels;

  // StarTrekker FLT8 stores each logical 8-channel pattern as two consecutive
  // 4-channel patterns, so the order table indexes half as many real patterns.
  const storedChannels = variant.splitPatterns ? 4 : channels;
  const storedPatternCount = variant.splitPatterns ? patternCount * 2 : patternCount;

  // --- Patterns -----------------------------------------------------------
  r.seek(headerSize);
  const patterns = [];
  let offTablePeriods = 0;

  if (variant.splitPatterns) {
    for (let p = 0; p < patternCount; p++) {
      const pattern = createPattern(ROWS_PER_PATTERN, channels);
      for (let half = 0; half < 2; half++) {
        for (let row = 0; row < ROWS_PER_PATTERN; row++) {
          for (let ch = 0; ch < 4; ch++) {
            const dst = (row * channels + half * 4 + ch) * CELL_STRIDE;
            offTablePeriods += readCell(r, pattern.data, dst);
          }
        }
      }
      patterns.push(pattern);
    }
  } else {
    for (let p = 0; p < storedPatternCount; p++) {
      const pattern = createPattern(ROWS_PER_PATTERN, channels);
      for (let row = 0; row < ROWS_PER_PATTERN; row++) {
        for (let ch = 0; ch < storedChannels; ch++) {
          const dst = (row * channels + ch) * CELL_STRIDE;
          offTablePeriods += readCell(r, pattern.data, dst);
        }
      }
      patterns.push(pattern);
    }
  }
  patternCount = patterns.length;

  if (offTablePeriods > 0) {
    warnings.push({
      code: 'period',
      message: `${offTablePeriods} note(s) used periods outside the ProTracker table; snapped to the nearest note.`,
    });
  }

  // --- Sample data --------------------------------------------------------
  const instruments = [];
  for (let i = 0; i < sampleCount; i++) {
    const rs = raw[i];
    const inst = createInstrument(rs.name);
    const smp = createSample(rs.name);
    smp.volume = rs.volume;
    smp.finetune = rs.finetune * 16; // MOD's -8..7 widened to XM's ±128
    smp.bits = 8;
    smp.panning = 128;

    const available = Math.max(0, Math.min(rs.length, r.remaining));
    if (available < rs.length && rs.length > 0) {
      warnings.push({
        code: 'truncated',
        message: `Sample ${i + 1} ("${rs.name}") is truncated: header claims ${rs.length} bytes, file has ${available}.`,
      });
    }
    const bytes = r.sub(rs.length);
    const data = new Float32Array(available);
    for (let n = 0; n < available; n++) {
      const v = bytes[n];
      data[n] = (v > 127 ? v - 256 : v) / 128;
    }
    smp.data = data;
    smp.length = available;

    // A loop length of one word is ProTracker's idiom for "no loop".
    if (rs.loopLength > 2 && rs.loopStart + rs.loopLength <= available + 2) {
      smp.loopType = LOOP_FORWARD;
      smp.loopStart = Math.min(rs.loopStart, available);
      smp.loopLength = Math.min(rs.loopLength, available - smp.loopStart);
      if (smp.loopLength <= 2) {
        smp.loopType = LOOP_NONE;
        smp.loopStart = 0;
        smp.loopLength = 0;
      }
    } else {
      smp.loopType = LOOP_NONE;
    }

    inst.samples = [smp];
    inst.sampleMap = new Uint8Array(96); // every note plays sample 0
    instruments.push(inst);
  }

  // MOD panning is the Amiga's hard LRRL; the mixer softens it to taste.
  const defaultPanning = new Uint8Array(channels);
  for (let i = 0; i < channels; i++) {
    const lr = (i & 3) === 0 || (i & 3) === 3;
    defaultPanning[i] = lr ? 0 : 255;
  }

  const song: Song = {
    name: title,
    trackerName: variant.label,
    format: 'mod',
    mode: 'pt',
    channels,
    order,
    songLength,
    restartPosition: restartByte < songLength ? restartByte : 0,
    patterns,
    instruments,
    initialSpeed: 6,
    initialTempo: 125,
    linearFrequency: false,
    amigaLimits: true,
    defaultPanning,
  };

  return { song, warnings };
}

/**
 * Read one 4-byte MOD cell into the flat pattern array.
 * Returns 1 if the period was not a ProTracker table value, else 0.
 */
function readCell(r: ByteReader, data: Uint8Array, dst: number): number {
  const b0 = r.u8();
  const b1 = r.u8();
  const b2 = r.u8();
  const b3 = r.u8();

  const period = ((b0 & 0x0f) << 8) | b1;
  const instrument = (b0 & 0xf0) | (b2 >> 4);
  const effect = b2 & 0x0f;
  const param = b3;

  let odd = 0;
  if (period > 0) {
    const note = periodToNote(period);
    data[dst + CELL_NOTE] = note + 1; // stored 1-based, 0 means "no note"
    if (isOffTablePeriod(period)) odd = 1;
  }
  data[dst + CELL_INSTRUMENT] = instrument;
  data[dst + CELL_EFFECT] = effect;
  data[dst + CELL_PARAM] = param;
  return odd;
}
